/* eslint-disable @typescript-eslint/ban-ts-comment */
import { createStore } from "vuex";
import { allHooks } from "@/Api/Hooks";
import { getMoleculesFromStore, setupExternalStoreAccess, } from "./StoreExternalAccess";
import { newTreeNodeList } from "@/TreeNodes/TreeNodeMakers";
import { setStoreIsDirty } from "@/Core/SaveOnClose/DirtyStore";
import { incrementTreeVersion } from "@/TreeNodes/TreeCache";
import { updateDocumentTitle } from "@/Core/GlobalVars";
const _commonMutations = {
    /**
     * Sets a state variable.
     *
     * @param {any}         state    The state.
     * @param {NameValPair} payload  The name and value to set.
     */
    setVar(state, payload) {
        if (payload.module) {
            state[payload.module][payload.name] = payload.val;
        }
        else {
            state[payload.name] = payload.val;
        }
    },
    /**
     * Adds a value to a list in the state.
     *
     * @param {any}         state    The state.
     * @param {NameValPair} payload  The name and value to push to the list.
     */
    pushToList(state, payload) {
        // If the list is molecules, use the dedicated mutation which handles reactivity.
        if (payload.name === "molecules") {
            this.commit("pushToMolecules", payload.val);
            return;
        }
        // Generic list push for other state arrays like 'log'.
        if (Array.isArray(payload.val)) {
            state[payload.name].push(...payload.val);
        }
        else {
            state[payload.name].push(payload.val);
        }
    },
    /**
     * Adds a value to a list in the state.
     *
     * @param {any}          state  The state.
     * @param {TreeNodeList} mols   The name and value to push to the list.
     */
    pushToMolecules(state, mols) {
        // @ts-ignore
        if (mols.title) {
            // It's a tree node
            state.molecules.push(mols);
        }
        else {
            // It's TreeNodeList
            state.molecules.extend(mols);
        }
        // Since it's an object, need to trigger reactivity. TODO: No way to do
        // this via deep?
        state.molecules = state.molecules.copy.shallow;
    },
    /**
     * Adds multiple TreeNode objects to the molecules list in a single
     * mutation, producing only one shallow copy and one reactivity trigger.
     * This is critical for batch loading where per-node pushes cause O(n^2)
     * copy overhead and redundant cache invalidations.
     *
     * @param {any}        state  The state.
     * @param {TreeNode[]} nodes  The nodes to add.
     */
    pushToMoleculesBulk(state, nodes) {
        const molecules = state.molecules;
        // Clear focus on existing molecules so the viewer zooms to the
        // newly added batch.
        molecules.flattened.forEach((node) => {
            node.focused = false;
        });
        for (const node of nodes) {
            node.focused = true;
            molecules.push(node);
        }
        // Single shallow copy triggers Vue reactivity exactly once.
        state.molecules = molecules.copy.shallow;
    },
    /**
     * Adds a property (value) to an object in the state.
     *
     * @param {any}         state    The state.
     * @param {NameValPair} payload  The name and property (value) to add.
     */
    // addToObj(state: any, payload: NameValPair) {
    //     state[payload.name] = {
    //         ...state[payload.name],
    //         ...payload.val,
    //     };
    // },
    /**
     * Updates the molecules in the state.
     *
     * @param {any}             state  The state.
     * @param {TreeNodeList} mols   The updated molecules.
     */
    updateMolecules(state, mols) {
        // state.molecules = mols;
        // remove entries in state.molecules
        while (state.molecules.length > 0) {
            state.molecules.pop();
        }
        // Add in new items
        state.molecules.extend(mols);
    },
};
const _modules = {};
/**
 * Add a module to the store.
 *
 * @param  {string} moduleName The name of the module to add to the vuex store.
 * @param  {any}    vals       The state variables to include in this module.
 */
export function addVueXStoreModule(moduleName, vals) {
    _modules[moduleName] = {
        namespaced: true,
        state: vals,
        mutations: _commonMutations,
    };
}
export let store;
/**
 * Sets up the vuex store.
 *
 * @returns {Store} The vuex store.
 */
export function setupVueXStore() {
    const storeVars = {
        state: {
            molecules: newTreeNodeList([]),
            log: [],
            goldenLayout: undefined,
            viewerVantagePoint: undefined,
            projectTitle: "",
            updateZoom: true,
            molViewer: "3dmol",
            undoStack: [newTreeNodeList([])],
            redoStack: [],
        },
        getters: {},
        mutations: {
            ..._commonMutations,
            /**
             * Clear the molecule that's the focus of the viewer.
             *
             * @param {any}     state         The state.
             * @param {boolean} [updateZoom]  Whether to also update the zoom.
             */
            clearFocusedMolecule(state, updateZoom = true) {
                if (!updateZoom) {
                    state["updateZoom"] = false;
                }
                state["molecules"].flattened.forEach((node) => {
                    node.focused = false;
                });
                // Revert updateZoom after rerender
                if (!updateZoom) {
                    setTimeout(() => {
                        state["updateZoom"] = true;
                    }, 0);
                }
            },
        },
        actions: {},
        modules: _modules,
    };
    store = createStore(storeVars);
    store.watch((state) => state.projectTitle, (newTitle) => {
        updateDocumentTitle(newTitle);
    }, { immediate: true });
    store.watch(
    // When the returned result changes...
    function (state) {
        return state.molecules;
    }, 
    // Run this callback
    (molecules) => {
        incrementTreeVersion(); // This invalidates all caches.
        allHooks.onMoleculesChanged.forEach((func) => {
            func(molecules);
        });
    }, { deep: true });
    store.watch(function (state) {
        return state;
    }, ( /* _state: any */) => {
        if (getMoleculesFromStore().length == 0) {
            return;
        }
        setStoreIsDirty(true);
    }, { deep: true });
    setupExternalStoreAccess(store);
    // For debugging
    // @ts-ignore
    window.store = store;
    return store;
}
